/**
 * Pure numbers behind the game-page sheets (drop out, use a shield, how
 * selection works). Kept free of React so they can be unit tested.
 */
import { calculateStreakBonus } from '../../../utils/xpBreakdown';

/** Streak XP bonus as a whole percentage, using the real (tapering) curve. */
export const streakBonusPct = (streak: number): number => Math.round(calculateStreakBonus(Math.max(0, streak)) * 100);

/** Games back until a shield-protected streak meets the natural one: ceil(protected / 2). */
export const recoveryGames = (protectedStreak: number): number => Math.ceil(Math.max(0, protectedStreak) / 2);

/**
 * Effective streak `gamesBack` games after returning on a shield. Matches
 * useShieldStatus / use_shield_token: MAX(natural, protected − natural), and
 * natural is the number of games played since coming back.
 */
export const shieldedStreakAfter = (protectedStreak: number, gamesBack: number): number =>
  Math.max(gamesBack, protectedStreak - gamesBack);

/**
 * Which "games back" to show, at most `max` of them: every game up to one past
 * the meeting point, or an even spread that always keeps game 1, the meeting
 * point and the game after it.
 */
export function sampleGamesBack(protectedStreak: number, max = 6): number[] {
  const meet = Math.max(recoveryGames(protectedStreak), 1);
  const last = Math.max(meet + 1, 3);
  if (last <= max) return Array.from({ length: last }, (_, i) => i + 1);
  const keys = new Set<number>();
  const span = meet - 1;
  for (let i = 0; i < max - 1; i++) keys.add(Math.round(1 + (i * span) / (max - 2)));
  keys.add(meet);
  keys.add(meet + 1);
  return [...keys].sort((a, b) => a - b);
}

export interface BonusBar {
  /** Games played since coming back. */
  game: number;
  /** Streak that counts for XP at that point. */
  streak: number;
  pct: number;
  /** 'shield' while protection is still above the natural streak, 'natural' after, 'reset' without a shield. */
  kind: 'shield' | 'natural' | 'reset';
}

/** XP bonus over the next games back, with or without a shield on the current streak. */
export function dropoutBars(streak: number, withShield: boolean, max = 6): BonusBar[] {
  const meet = recoveryGames(streak);
  return sampleGamesBack(streak, max).map((game) => {
    const s = withShield ? shieldedStreakAfter(streak, game) : game;
    return {
      game,
      streak: s,
      pct: streakBonusPct(s),
      kind: !withShield ? 'reset' : game >= meet ? 'natural' : 'shield',
    };
  });
}

export interface DecayRow {
  game: number;
  /** Natural streak (games played since coming back). */
  natural: number;
  /** How far shield protection lifts the streak above the natural one (0 once they meet). */
  lift: number;
  pct: number;
}

/** "How you rebuild" rows: natural streak plus the shield's lift, as fractions of the protected streak. */
export function decayRows(protectedStreak: number, max = 6): DecayRow[] {
  return sampleGamesBack(protectedStreak, max).map((game) => {
    const effective = shieldedStreakAfter(protectedStreak, game);
    return { game, natural: game, lift: effective - game, pct: streakBonusPct(effective) };
  });
}

export interface SlotSegment {
  key: 'token' | 'merit' | 'random';
  slots: number;
}

/**
 * How a game's slots split between priority tokens, merit (XP) and the random
 * draw. Token slots come out of the merit pool, never the random slots
 * (select_players_for_game / playerSelection.ts).
 */
export function selectionSlots(maxPlayers: number, randomSlots: number, tokenCount: number): SlotSegment[] {
  const random = Math.max(0, Math.min(randomSlots, maxPlayers));
  const xpSlots = Math.max(0, maxPlayers - random);
  const tokens = Math.max(0, Math.min(tokenCount, xpSlots));
  return [
    { key: 'token', slots: tokens },
    { key: 'merit', slots: xpSlots - tokens },
    { key: 'random', slots: random },
  ];
}

/** Injury claims are for the game just played: the approved rule is to claim within 48 hours. */
export const INJURY_CLAIM_WINDOW_HOURS = 48;

export const withinInjuryClaimWindow = (gameDate: string | Date, now: Date = new Date()): boolean => {
  const kickOff = new Date(gameDate).getTime();
  if (Number.isNaN(kickOff)) return false;
  const hours = (now.getTime() - kickOff) / 3_600_000;
  return hours >= 0 && hours <= INJURY_CLAIM_WINDOW_HOURS;
};
